/**
 * PDF Exporter
 * Handles PDF generation with formatted charts and tables
 * 
 * Uses jspdf for PDF creation and jspdf-autotable for table formatting.
 * Charts are captured using html-to-image and embedded as images.
 */

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { toJpeg } from "html-to-image";
import moment from "moment";

import { 
    ExportableData, 
    ExportableChart, 
    ExportPayload, 
    PdfExportOptions,
    ExportColumn
} from "./types";
import { AnalyticsFilters } from "../../types/analytics";
import Logger from "../../utils/Logger";

/**
 * Default PDF export options
 */
const DEFAULT_PDF_OPTIONS: PdfExportOptions = {
    orientation: "landscape",
    includeCharts: true,
    includeTables: true,
    pageSize: "a4",
    headerColor: "#4a90d9",
    includeTimestamp: true,
    includeFilters: true
};

/**
 * PDF page dimensions (in mm)
 */
const PAGE_DIMENSIONS = {
    a4: { portrait: { width: 210, height: 297 }, landscape: { width: 297, height: 210 } },
    letter: { portrait: { width: 216, height: 279 }, landscape: { width: 279, height: 216 } }
};

/**
 * Format a cell value for PDF display
 */
function formatCellValue(value: unknown, column: ExportColumn): string {
    if (value === null || value === undefined) {
        return "-";
    }

    switch (column.format) {
        case "number": {
            const num = typeof value === "number" ? value : Number(value);
            return isNaN(num) ? "-" : num.toLocaleString();
        }
        case "percent": {
            const pct = typeof value === "number" ? value : Number(value);
            return isNaN(pct) ? "-" : `${pct.toFixed(2)}%`;
        }
        case "date":
            return moment(String(value)).format("MMM D, YYYY");
        case "text":
        default:
            return String(value);
    }
}

/**
 * Format filters for display in PDF header
 */
function formatFilters(filters: AnalyticsFilters): string[] {
    const lines: string[] = [];

    if (filters.startDate && filters.endDate) {
        lines.push(`Date Range: ${moment(filters.startDate).format("MMM D, YYYY")} - ${moment(filters.endDate).format("MMM D, YYYY")}`);
    }

    if (filters.attributionId && filters.attributionId.length > 0) {
        lines.push(`Attribution: ${filters.attributionId.join(", ")}`);
    }

    if (filters.brandId && filters.brandId.length > 0) {
        lines.push(`Brand: ${filters.brandId.join(", ")}`);
    }

    if (filters.deviceType && filters.deviceType.length > 0) {
        lines.push(`Device: ${filters.deviceType.join(", ")}`);
    }

    return lines;
}

/**
 * Capture a DOM element as a PNG data URL
 */
export async function captureChartImage(element: HTMLElement): Promise<string> {
    try {
        // Use JPEG for smaller file sizes with good quality
        const dataUrl = await toJpeg(element, {
            backgroundColor: "#ffffff",
            pixelRatio: 1.5, // Balanced resolution for quality vs size
            quality: 0.85, // JPEG quality (0-1)
            skipFonts: true // Avoid font embedding issues
        });
        return dataUrl;
    } catch (error) {
        Logger.log("error", "PDF Export", "Failed to capture chart image", error);
        throw new Error("Failed to capture chart for PDF export");
    }
}

/**
 * Generate filename for PDF export
 */
function generateFilename(title: string, customFilename?: string): string {
    if (customFilename) {
        return customFilename.replace(/\.pdf$/i, "") + ".pdf";
    }

    const timestamp = moment().format("YYYYMMDD_HHmmss");
    const titleSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");

    return `${titleSlug}_${timestamp}.pdf`;
}

/**
 * Add header section to PDF
 */
function addHeader(
    doc: jsPDF,
    title: string,
    subtitle: string | undefined,
    options: PdfExportOptions,
    currentY: number
): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = currentY;

    // Title
    doc.setFontSize(20);
    doc.setTextColor(51, 51, 51);
    doc.text(title, pageWidth / 2, y, { align: "center" });
    y += 10;

    // Subtitle
    if (subtitle) {
        doc.setFontSize(12);
        doc.setTextColor(102, 102, 102);
        doc.text(subtitle, pageWidth / 2, y, { align: "center" });
        y += 8;
    }

    // Timestamp
    if (options.includeTimestamp) {
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        doc.text(
            `Generated: ${moment().format("MMMM D, YYYY [at] h:mm A")}`,
            pageWidth / 2,
            y,
            { align: "center" }
        );
        y += 8;
    }

    return y;
}

/**
 * Add filter summary to PDF
 */
function addFilterSummary(
    doc: jsPDF,
    filters: AnalyticsFilters,
    currentY: number
): number {
    const filterLines = formatFilters(filters);
    
    if (filterLines.length === 0) {
        return currentY;
    }

    let y = currentY + 5;

    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text("Applied Filters:", 14, y);
    y += 5;

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    filterLines.forEach(line => {
        doc.text(`  • ${line}`, 14, y);
        y += 4;
    });

    return y + 5;
}

/**
 * Add a data table to PDF
 */
function addTable(
    doc: jsPDF,
    data: ExportableData,
    options: PdfExportOptions,
    currentY: number
): number {
    let y = currentY;

    // Table title
    if (data.title) {
        doc.setFontSize(14);
        doc.setTextColor(51, 51, 51);
        doc.text(data.title, 14, y);
        y += 8;
    }

    // Table description
    if (data.description) {
        doc.setFontSize(10);
        doc.setTextColor(102, 102, 102);
        doc.text(data.description, 14, y);
        y += 6;
    }

    // Prepare table data
    const headers = data.columns.map(col => col.label);
    const rows = data.rows.map(row =>
        data.columns.map(col => formatCellValue(row[col.key], col))
    );

    // Parse header color
    const headerColor = options.headerColor || "#4a90d9";
    const r = parseInt(headerColor.slice(1, 3), 16);
    const g = parseInt(headerColor.slice(3, 5), 16);
    const b = parseInt(headerColor.slice(5, 7), 16);

    // Add table using autoTable
    autoTable(doc, {
        startY: y,
        head: [headers],
        body: rows,
        styles: {
            fontSize: 9,
            cellPadding: 3
        },
        headStyles: {
            fillColor: [r, g, b],
            textColor: [255, 255, 255],
            fontStyle: "bold"
        },
        alternateRowStyles: {
            fillColor: [245, 245, 245]
        },
        margin: { left: 14, right: 14 }
    });

    // Get the final Y position after table
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const finalY = (doc as any).lastAutoTable?.finalY || y + 50;
    
    return finalY + 10;
}

/**
 * Add a chart image to PDF
 */
function addChart(
    doc: jsPDF,
    chart: ExportableChart,
    currentY: number
): number {
    let y = currentY;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const maxWidth = pageWidth - (margin * 2);
    const maxHeight = (pageHeight - y - margin) * 0.6; // Use 60% of remaining space max

    // Chart title
    if (chart.title) {
        doc.setFontSize(14);
        doc.setTextColor(51, 51, 51);
        doc.text(chart.title, margin, y);
        y += 8;
    }

    // Add chart image if available
    if (chart.imageDataUrl) {
        // Calculate dimensions maintaining aspect ratio
        const img = new Image();
        img.src = chart.imageDataUrl;
        
        // Default to reasonable dimensions if image dimensions aren't available
        const aspectRatio = 16 / 9;
        let imgWidth = maxWidth;
        let imgHeight = imgWidth / aspectRatio;

        if (imgHeight > maxHeight) {
            imgHeight = maxHeight;
            imgWidth = imgHeight * aspectRatio;
        }

        // Center the image
        const xOffset = (pageWidth - imgWidth) / 2;

        doc.addImage(chart.imageDataUrl, "JPEG", xOffset, y, imgWidth, imgHeight);
        y += imgHeight + 10;
    }

    return y;
}

/**
 * Export analytics data to PDF and trigger download
 */
export async function exportToPdf(
    payload: ExportPayload,
    options: PdfExportOptions = {}
): Promise<void> {
    const mergedOptions = { ...DEFAULT_PDF_OPTIONS, ...options };

    try {
        const pageSize = mergedOptions.pageSize || "a4";
        const orientation = mergedOptions.orientation || "landscape";
        const dimensions = PAGE_DIMENSIONS[pageSize][orientation];

        const doc = new jsPDF({
            orientation,
            unit: "mm",
            format: pageSize
        });

        let currentY = 20;

        // Add header
        currentY = addHeader(doc, payload.title, payload.subtitle, mergedOptions, currentY);

        // Add filter summary
        if (mergedOptions.includeFilters && payload.filters) {
            currentY = addFilterSummary(doc, payload.filters, currentY);
        }

        currentY += 5;

        // Add charts
        if (mergedOptions.includeCharts && payload.charts.length > 0) {
            for (const chart of payload.charts) {
                // Check if we need a new page
                if (currentY > dimensions.height - 60) {
                    doc.addPage();
                    currentY = 20;
                }

                currentY = addChart(doc, chart, currentY);
            }
        }

        // Add tables
        if (mergedOptions.includeTables && payload.tables.length > 0) {
            for (const table of payload.tables) {
                // Check if we need a new page
                if (currentY > dimensions.height - 60) {
                    doc.addPage();
                    currentY = 20;
                }

                currentY = addTable(doc, table, mergedOptions, currentY);
            }
        }

        // Save the PDF
        const filename = generateFilename(payload.title, mergedOptions.filename);
        doc.save(filename);

        Logger.log("info", "PDF Export", `Exported PDF: ${filename}`);
    } catch (error) {
        Logger.log("error", "PDF Export", "Failed to export PDF", error);
        throw new Error("Failed to export data to PDF");
    }
}

/**
 * Export single table data to PDF
 */
export async function exportTableToPdf(
    data: ExportableData,
    options: PdfExportOptions = {}
): Promise<void> {
    const payload: ExportPayload = {
        title: data.title,
        generatedAt: moment().toISOString(),
        tables: [data],
        charts: [],
        filters: data.metadata?.filters
    };

    return exportToPdf(payload, options);
}

/**
 * Export charts with their data tables to PDF
 */
export async function exportChartsToPdf(
    charts: ExportableChart[],
    tables: ExportableData[],
    title: string,
    filters?: AnalyticsFilters,
    options: PdfExportOptions = {}
): Promise<void> {
    const payload: ExportPayload = {
        title,
        generatedAt: moment().toISOString(),
        tables,
        charts,
        filters
    };

    return exportToPdf(payload, options);
}

/**
 * Export visual screenshot of a container element to PDF
 * This captures the container as it appears on screen
 */
export async function exportVisualToPdf(
    containerElement: HTMLElement,
    title: string,
    filters?: AnalyticsFilters,
    options: PdfExportOptions = {}
): Promise<void> {
    const mergedOptions = { ...DEFAULT_PDF_OPTIONS, ...options };

    try {
        Logger.log("info", "PDF Export", "Starting visual PDF export");

        // Capture the container as an image using JPEG for smaller file size
        const imageDataUrl = await toJpeg(containerElement, {
            backgroundColor: "#ffffff",
            pixelRatio: 1.5, // Balanced resolution for quality vs size (was 2)
            quality: 0.85, // JPEG quality setting (0-1) for good quality with compression
            skipFonts: true,
            cacheBust: true,
            // Filter out any elements that shouldn't be in the export (like the export button itself)
            filter: (node) => {
                if (node instanceof Element) {
                    // Skip export-related buttons
                    if (node.id === "export-dropdown" || node.closest("#export-dropdown")) {
                        return false;
                    }
                    // Skip any element with data-export-ignore attribute
                    if (node.hasAttribute("data-export-ignore")) {
                        return false;
                    }
                }
                return true;
            }
        });

        // Get image dimensions to calculate aspect ratio
        const img = new Image();
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imageDataUrl;
        });
        
        const aspectRatio = img.width / img.height;

        const pageSize = mergedOptions.pageSize || "a4";
        const orientation = mergedOptions.orientation || "landscape";
        const dimensions = PAGE_DIMENSIONS[pageSize][orientation];

        const doc = new jsPDF({
            orientation,
            unit: "mm",
            format: pageSize
        });

        const margin = 14;
        let currentY = 20;

        // Add header
        currentY = addHeader(doc, title, undefined, mergedOptions, currentY);

        // Add filter summary
        if (mergedOptions.includeFilters && filters) {
            currentY = addFilterSummary(doc, filters, currentY);
        }

        currentY += 5;

        // Calculate available space for the image
        const availableWidth = dimensions.width - (margin * 2);
        const availableHeight = dimensions.height - currentY - margin;

        // Calculate image dimensions maintaining aspect ratio
        let imgWidth = availableWidth;
        let imgHeight = imgWidth / aspectRatio;

        // If height exceeds available space, scale down
        if (imgHeight > availableHeight) {
            imgHeight = availableHeight;
            imgWidth = imgHeight * aspectRatio;
        }

        // Center the image horizontally
        const xOffset = (dimensions.width - imgWidth) / 2;

        // Add the screenshot image (JPEG format for smaller size)
        doc.addImage(imageDataUrl, "JPEG", xOffset, currentY, imgWidth, imgHeight);

        // Save the PDF
        const filename = generateFilename(title, mergedOptions.filename);
        doc.save(filename);

        Logger.log("info", "PDF Export", `Exported visual PDF: ${filename}`);
    } catch (error) {
        Logger.log("error", "PDF Export", "Failed to export visual PDF", error);
        throw new Error("Failed to export visual PDF. Please try again.");
    }
}
